<template>
  <assessment
    :current-participant="currentParticipant"
    :current-exercise="currentExercise"
    :current-matrix='runInfo.competencyExerciseMatrix.filter((ce) => ce.exerciseId === currentExerciseId)'
    @saveWithDelay='saveWithDelay'
  />
</template>

<script>
  import { mapState, mapGetters } from 'vuex';
  import { EventBus, EVENTS } from '@/libs/eventBus';
  import Assessment from '../components/Assessment'
  import ACTIONS from '../storeActions';


  export default {
    name: 'RatingPage',
    components: {
      Assessment,
    },
    computed: {
      ...mapGetters(['currentExercise', 'currentParticipant', 'syncFromAppToCockpitAllowed', 'currentObserver', 'isGuestObserver']),
      ...mapState(['currentParticipantId', 'currentExerciseId', 'assessments', 'currentInterviewId', 'runInfo']),
    },
    data() {
      return {
        saveTimer: {},
        assessmentsToSave: {}
      }
    },
    methods: {
      /**
       * @desc triggers a delayed save for a specific assessment
       * @param {AssessmentDef} assessment
       */
      saveWithDelay(assessment) {
        const self = this;
        self.assessmentsToSave[assessment.temporaryId] = assessment
        if (self.saveTimer[assessment.temporaryId]) {
          clearTimeout(self.saveTimer[assessment.temporaryId]);
          self.saveTimer[assessment.temporaryId] = undefined
        }
        self.saveTimer[assessment.temporaryId] = setTimeout(() => {
          self.save(assessment.temporaryId)
        }, 3000);
      },
      /**
       * @desc saves the modified assessment/s to the server
       * @param {string} [assessmentId] - if an assessment is passed it will only save this one
       */
      save(assessmentId) {
        // todo handle put / post differently
        const self = this;
        const handleSave = (method, items) => {
          if (!this.syncFromAppToCockpitAllowed || this.isGuestObserver) {
            console.log('Skipping sync - not allowed currently');
            const update = Array.isArray(items) ? items : [items]
            self.$store.dispatch(ACTIONS.UPDATE_ASSESSMENTS, { current: update, response: update })
            return;
          }
          /**
           * @typedef {Object} items
           * @property { number } MatrixId
           * @property { number } ParticipantId
           * @property { number } SelectedValue
           */
          self.$apiRequest[method]('/assessments', Array.isArray(items) ? items : [items])
            .then((data) => {
              self.$store.dispatch(ACTIONS.UPDATE_ASSESSMENTS, { current: Array.isArray(items) ? items : [items], response: data })
            })
            .catch((err) => {
              console.error(err);
              self.$store.dispatch(ACTIONS.SET_SNACKBAR, { text: self.$t('Errors.unknown'), color: 'error' })
            }).finally(() => {
              if (Array.isArray(items)) {
                if (items.length <= 0) return;
                for (const item of items) {
                  if (self.assessmentsToSave[item.temporaryId]) delete self.assessmentsToSave[item.temporaryId]
                }
              } else if (self.assessmentsToSave[items.temporaryId]) delete self.assessmentsToSave[items.temporaryId]
          })
        }
        if (!assessmentId) {
          const modified = [];
          Object.keys(self.saveTimer).forEach((key) => {
            if (self.saveTimer[key] === undefined) return;
            clearTimeout(self.saveTimer[key]);
            self.saveTimer[key] = undefined;
            let assessment = self.assessments.find((a) => a.temporaryId === key);
            if (!assessment) assessment = self.assessmentsToSave[key];
            modified.push(assessment)
          })
          // split modified assessments into update and create groups
          const toCreate = modified.filter( (m) => !m.loaded );
          const toUpdate = modified.filter( (m) => m.loaded );
          if ( toCreate.length > 0 ) handleSave( 'post', toCreate );
          if ( toUpdate.length > 0 ) handleSave( 'put', toUpdate );
          return;
        }
        if ( self.saveTimer[assessmentId] ) {
          clearTimeout(self.saveTimer[assessmentId])
          self.saveTimer[assessmentId] = undefined;
        }
        const assessment = self.assessmentsToSave[assessmentId]
        handleSave('put', assessment)
      },
    },
    beforeRouteLeave(to, from, next) {
      // todo trigger save directly on leave
      next();
    },
    mounted() {
      const self = this;
      if (this.currentInterviewId) this.$router.push({ name: 'interview' })
      if (!this.currentParticipantId || !this.currentExerciseId) {
        this.$store.dispatch(ACTIONS.SET_SNACKBAR, { text: this.$i18n.t('Errors.noExerciseSelected'), color: 'error' } );
        this.$router.push({ name: 'overview' });
      }
      EventBus.$on(EVENTS.ASSESSMENT_SAVE, () => {
        if (Object.keys(self.saveTimer).length > 0) self.save()
      })
    },
    updated() {
      // Scroll to top, since we are switching content
      this.$vuetify.goTo('body', 0, 500);
      //console.log('assessment updated');
    }
  }
</script>
